import { VerificationError } from './errors'
import {
  normalizeURIParts,
  prepareRequestOptions,
  getRequestMethod,
} from './request'
import {
  isTrue,
  safeParse,
  stringEmpty,
  objectToSortedString,
} from './display'
import { WebCrypto } from './WebCrypto'

const getUnixString = () => {
  const currentDate = new Date()
  const unixTimestamp = currentDate.getTime()
  return Math.floor(unixTimestamp / 1000).toString()
}

export const getVerificationKeysData = async (publicKey, { crypto: _crypto, util: _util } = {}) => {
  if (stringEmpty(publicKey)) {
    return {}
  }

  const webCrypto = new WebCrypto({ crypto: _crypto, util: _util })

  const _ephemeralStoreableKeyPair = await webCrypto.getStorableKeyPair(
    await webCrypto.generateKeyPair()
  )

  const _verificationKeyPair = await webCrypto.getVerificationKeys({
    publicKey,
    privateKey: _ephemeralStoreableKeyPair.privateKey,
  })

  return {
    publicKey,
    ephemeral: _ephemeralStoreableKeyPair,
    verification: _verificationKeyPair,
  }
}

// eslint-disable-next-line arrow-body-style
export const prepareVerificationRequest = ({ keysData: _keysData, disableRecryption: _disableRecryption, crypto: _crypto, util: _util } = {}) => {
  const webCrypto = new WebCrypto({ crypto: _crypto, util: _util })
  const disableRecryption = isTrue(_disableRecryption)

  return async (requestPath, { method: rawMethod, body: rawBody, headers: rawHeaders,  ...requestOptions } = {}) => {
    let parsedBody = safeParse(rawBody)
    const method = getRequestMethod(parsedBody, rawMethod)

    if (disableRecryption || stringEmpty(_keysData?.publicKey)) {
      return [
        requestPath,
        prepareRequestOptions({
          method,
          body: parsedBody,
          headers: rawHeaders,
          ...requestOptions,
        }, requestPath)
      ]
    }

    const {
      verification: {
        derivedHMACKey,
        derivedSecretKey,
      } = {},
      ephemeral: {
        publicKey: ephemeralPublicKey,
      } = {},
    } = _keysData ?? {}

    if (!derivedHMACKey || !ephemeralPublicKey) {
      return undefined
    }

    if (parsedBody && derivedSecretKey) {
      parsedBody = await webCrypto.encryptMessage(JSON.stringify(parsedBody), derivedSecretKey)
    }

    const timestamp = getUnixString()
    const computedHMAC = await webCrypto.generateHMAC(
      objectToSortedString({
        body: parsedBody,
        method,
        timestamp,
        ...normalizeURIParts(requestPath),
      }),
      derivedHMACKey
    )

    return [
      requestPath,
      prepareRequestOptions({
        method,
        body: parsedBody,
        headers: {
          'X-Authorization': `HMAC-SHA256 ${computedHMAC}`,
          'X-Authorization-Timestamp': timestamp,
          'X-Ephemeral-Key': ephemeralPublicKey,
          'X-Public-Key': _keysData.publicKey,
          ...rawHeaders,
        },
        ...requestOptions,
      }, requestPath),
      derivedSecretKey
    ]
  }
}

export const processVerificationResponse = ({ keysData, disableRecryption: _disableRecryption, crypto: _crypto, util: _util } = {}) => {
  const webCrypto = new WebCrypto({ crypto: _crypto, util: _util })
  const disableRecryption = isTrue(_disableRecryption)

  return async (encryptedResponse, _derivedSecretKey) => {
    const derivedSecretKey = _derivedSecretKey ?? keysData.verification?.derivedSecretKey
    if (disableRecryption || !encryptedResponse || !derivedSecretKey) {
      return encryptedResponse
    }
  
    try {
      const decryptedMessage = await webCrypto.decryptMessage(encryptedResponse, derivedSecretKey)
      return safeParse(decryptedMessage)
    } catch {
      throw new VerificationError('Error decrypting response', { code: 400 })
    }
  }
}

